import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AUTH_ROUTES } from "../components/authRoutes";
import { SETTINGS_ROUTES } from "../settingProfile/settingsRoutes";
import { DASHBOARD_ROUTES } from "./dashboardRoutes";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../hooks/useNotifications";
import { useSidebarData } from "../hooks/useSidebarData";
import usePreferredLanguageToggle from "../hooks/usePreferredLanguageToggle";
import { getStoredUser } from "../utils/authStorage";
import LogoutPopup from "../settingProfile/LogoutPopup";
import fallbackAvatar from "../assets/images/c1.png";
import "./CommonPageHeader.css";

/** Compact header for dashboard pages. Pass onBack or backTo for a stable back destination. */
const CommonPageHeader = ({ stateName, onBack, backTo, backState, embedded = false, showLocation = true, className = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { data: sidebarData } = useSidebarData();
  const { data: notifications } = useNotifications({ page: 1, unreadOnly: true });
  const storedUser = getStoredUser();
  const profile = sidebarData?.user_data || storedUser || {};
  const { currentPreferredLanguage, handleLanguageToggle, isPending: isLanguagePending } = usePreferredLanguageToggle(profile);
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return undefined;
    const closeMenu = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) setProfileOpen(false);
    };
    document.addEventListener("pointerdown", closeMenu);
    return () => document.removeEventListener("pointerdown", closeMenu);
  }, [profileOpen]);

  const label = stateName || location.state?.selectedState?.state_name || location.state?.state_name ||
    profile.state_name || profile.user_state_name || sidebarData?.selected_state?.state_name || "Select a state";
  const name = profile.name || profile.user_name || "Student";
  const avatar = profile.user_profile || profile.profile_image || profile.image || profile.user_image || profile.avatar || fallbackAvatar;
  const unreadCount = Number(notifications?.pagination?.totalRecords || 0);

  const goBack = () => {
    if (onBack) onBack();
    else if (backTo) navigate(backTo, { state: backState });
    else navigate(-1);
  };
  const openPage = (path) => {
    setProfileOpen(false);
    navigate(path);
  };
  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      setLogoutOpen(false);
      navigate(AUTH_ROUTES.signIn, { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <header className={`common-page-header${embedded ? " common-page-header--embedded" : ""} ${className}`.trim()}>
        <div className="common-page-header__location">
          <button type="button" className="common-page-header__back" onClick={goBack} aria-label="Go back">
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
          {showLocation && <span className="common-page-header__state"><i className="bi bi-geo-alt-fill" aria-hidden="true" />{label}</span>}
        </div>
        <div className="common-page-header__actions">
          <button type="button" className="common-page-header__icon common-page-header__language" onClick={handleLanguageToggle} disabled={isLanguagePending} aria-label={`Switch language to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`} title={`Current language: ${currentPreferredLanguage}`}>
            <i className="bi bi-globe" aria-hidden="true" /><span>{currentPreferredLanguage}</span>
          </button>
          <button type="button" className="common-page-header__icon common-page-header__notification" onClick={() => navigate(SETTINGS_ROUTES.notifications)} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}>
            <i className="bi bi-bell" aria-hidden="true" />{unreadCount > 0 && <span>{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </button>
          <div className="common-page-header__profile-wrap" ref={profileMenuRef} onKeyDown={(event) => { if (event.key === "Escape") setProfileOpen(false); }}>
            <button type="button" className="common-page-header__profile" onClick={() => setProfileOpen((open) => !open)} aria-label="Student profile" aria-haspopup="menu" aria-expanded={profileOpen}>
              <img src={avatar} alt="" onError={(event) => { event.currentTarget.src = fallbackAvatar; }} /><span>Hi, {name}</span><i className="bi bi-chevron-down" aria-hidden="true" />
            </button>
            {profileOpen && <div className="common-page-header__menu" role="menu" aria-label="Student profile menu">
              <button type="button" role="menuitem" onClick={() => openPage(SETTINGS_ROUTES.profile)}>My Profile</button>
              <button type="button" role="menuitem" onClick={() => openPage(DASHBOARD_ROUTES.home)}>My Progress</button>
              <button type="button" role="menuitem" onClick={() => openPage(SETTINGS_ROUTES.password)}>Settings</button>
              <button type="button" role="menuitem" onClick={() => { setProfileOpen(false); setLogoutOpen(true); }}>Logout</button>
            </div>}
          </div>
        </div>
      </header>
      <LogoutPopup open={logoutOpen} onClose={() => setLogoutOpen(false)} onConfirm={confirmLogout} isLoading={isLoggingOut} />
    </>
  );
};

export default CommonPageHeader;
